/**
 * analyticalQuery.js
 *
 * Phase 1 — Analytical Query Engine.
 *
 * Defines the shape of an AnalyticalQuery and the registry of what is
 * currently allowed to appear in one (datasets, measures per dataset,
 * dimensions per dataset, comparison types).
 *
 * This module does NOT fetch data and does NOT compute any financial
 * fact. It only defines and validates the shape of a question. Answering
 * the question is the SSOT Adapter's job (financialSSOTAdapter.js).
 *
 * Adding a new dataset/measure/dimension later means adding a registry
 * entry here — it never means adding calculation logic here.
 */

// ─── Registry: what exists today ──────────────────────────────────────────
//
// Only 'cost_performance' is active in this phase. The other datasets
// from the approved design (historical_comparison, commercial_contracts,
// concentration, and the still-blocked cash dataset) are intentionally
// NOT registered yet — they are added in later phases, one at a time,
// each through its own SSOT Adapter mapping and its own validation gate.

export const DATASETS = {
  cost_performance: {
    label: 'Cost Performance',
    owner: 'buildCumulativeData()',
    measures: ['budget', 'actual', 'eac', 'commitment', 'contractValue',
      'variance', 'grossProfit', 'budgetProfit', 'cpi'],
    // resource_type here has EXACTLY 4 values (material/labour/
    // subcontract/equipment) via buildCumulativeData()'s
    // resourceTypeTotals — a Resource with no matching resource_type is
    // silently excluded, confirmed by direct inspection of the Owner.
    // This is DIFFERENT from monthly_actual_cost's resource_type (5
    // values including 'Unclassified', via a separate Owner —
    // monthlyActualCostHierarchy.js — that never drops a row). The two
    // are NOT unified — see financialSSOTAdapter.js's
    // getResourceTypeRows() for the full rationale.
    //
    // sub_code/resource_code (added, this Stage): confirmed via direct
    // inspection of buildCumulativeData()'s subRows/resourceRows
    // construction (buildCostControlData.js, ~line 680/762) that each
    // child carries real, independently-computed values (bac/
    // totalActualCost/eac/commitment/cpi), not just hierarchy metadata —
    // see financialSSOTAdapter.js's sub_code/resource_code dimension
    // functions for the full trace. Flat grain (every Sub/Resource Code
    // across every included Cost Code, one row each — never grouped
    // under their parent).
    dimensions: ['cost_code', 'cost_type', 'package', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
  },
  historical_comparison: {
    label: 'Historical Comparison',
    owner: 'reconstructHierarchyFromSnapshotRows() + forecast_history (Cost Code grain only, v1)',
    // contractValue/grossProfit/budgetProfit deliberately excluded — not
    // frozen per-Cost-Code into forecast_history (project-level Reference/
    // Derived Metadata only, see historicalSSOTAdapter.js's Gate Review).
    measures: ['budget', 'actual', 'eac', 'commitment', 'variance', 'cpi'],
    // package/resource_type/indirect_category/sub_code intentionally NOT
    // included in v1. resource_type/indirect_category were implemented
    // during Step 4 and then WITHDRAWN after a Direct/Indirect
    // discriminator investigation found forecast_history has no reliable
    // way to distinguish a Direct Cost Code root from an Indirect one —
    // see historicalSSOTAdapter.js's file header for the full
    // investigation trail (resource_type is Resource-level only;
    // cost_type exists live but is never written to forecast_history).
    dimensions: ['cost_code'],
  },
  time_series: {
    label: 'Time Series',
    owner: 'buildCumulativeSeries() (Phase 4) via timeSeriesSSOTAdapter (Phase 5A)',
    // Unlike historical_comparison, contractValue/grossProfit/budgetProfit
    // ARE available — buildCumulativeSeries() calls the LIVE calculation
    // path (computeSnapshotFromFetchedData()) per point, not frozen
    // forecast_history rows, so meta.contractValue/grossProfit/
    // budgetProfit are always present (see timeSeriesSSOTAdapter.js's
    // header for the contractValue-is-constant-across-points caveat).
    measures: ['budget', 'actual', 'eac', 'commitment', 'contractValue',
      'variance', 'grossProfit', 'budgetProfit', 'cpi'],
    // package/sub_code intentionally NOT included in v1 — same grain
    // restriction as historical_comparison. resource_type/
    // indirect_category added (Step 4) — same posture as
    // cost_performance's Extension: 4 fixed resource_type keys (no
    // Unclassified), indirect_category includes 'Uncategorized'.
    // sub_code/resource_code added (this Stage) — same Owner
    // (buildCumulativeSeries() reuses computeSnapshotFromFetchedData()
    // per point), same confirmed real values as cost_performance's.
    dimensions: ['cost_code', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
  },
  monthly_actual_cost: {
    label: 'Monthly Actual Cost',
    owner: 'buildMonthlyActualCostData() + monthlyActualCostHierarchy.js',
    // Only one measure in v1 — see monthlyActualCostSSOTAdapter.js's
    // header for why `movement` (the Owner's own concept: last-two-
    // periods-present, not a calendar comparison) is deliberately NOT
    // exposed as a measure or comparison type here.
    measures: ['actual'],
    // resource_type here has up to 5 possible values (material/labour/
    // subcontract/equipment/Unclassified) — DELIBERATELY NOT unified
    // with cost_performance/time_series's resourceTypeTotals (4 values,
    // silently drops unclassified rows). This dataset's Owner
    // (resolveResourceType) never drops a row. See the Capability Audit
    // this dataset was added after — the difference is preserved, not
    // papered over.
    dimensions: ['cost_code', 'resource_type', 'indirect_category', 'sub_code', 'resource_code'],
    // Same call pattern as time_series/timeSeriesSSOTAdapter.js:
    // executeMonthlyActualCostQuery(projectId, periods, query) — periods
    // ("YYYY-MM" strings) is a separate parameter, exactly like
    // forecastDates is for executeTimeSeriesQuery(). query.time stays
    // null/unused, matching the standing "not used in Phase 1/2"
    // convention — no new Time syntax was introduced for this dataset.
    // Output is the same canonical TimeSeriesResult shape.
  },
}

export const COMPARISON_TYPES = {
  variance: {
    label: 'Variance',
    // baseline must be one of the dataset's own measures — validated
    // against the dataset's measures list, not hardcoded here.
  },
  // Reserved, NOT implemented in v1 — baseline/snapshot semantics for
  // these are explicitly undecided (see Sprint 6 design doc). Listing
  // them here only documents that the shape already accommodates them;
  // validateQuery() rejects them until they are actually implemented.
  // period_over_period: { label: 'Period over Period' },
  // forecast_revision:  { label: 'Forecast Revision' },
}

// ─── Measure Metadata (Phase 5B) ───────────────────────────────────────────
// Presentation-relevant metadata for each measure key, shared across every
// Visual type. This is NOT part of VisualDefinition (which stays minimal —
// query + type + optional yAxis) — format/label are properties of the
// MEASURE itself, not of any one chart displaying it, so they belong here,
// keyed the same way DATASETS' measures[] arrays are.
//
// format meanings:
//   currency    — SAR {value}, e.g. fmt() pattern already used in
//                 AlertsVariances.jsx / commercialAnalytics.js exceptions
//   percentage  — {value}%, e.g. Concentration Risk
//   ratio       — bare number, no symbol — e.g. cpi (typically ~0-2,
//                 NOT a percentage; treating it as one would misrepresent
//                 0.90 as "90%" instead of "0.90")
export const MEASURE_METADATA = {
  budget:         { label: 'Budget', format: 'currency' },
  actual:          { label: 'Actual', format: 'currency' },
  eac:              { label: 'EAC', format: 'currency' },
  commitment:        { label: 'Commitment', format: 'currency' },
  contractValue:      { label: 'Contract Value', format: 'currency' },
  variance:            { label: 'Variance', format: 'currency' },
  grossProfit:          { label: 'Gross Profit', format: 'currency' },
  budgetProfit:           { label: 'Budget Profit', format: 'currency' },
  cpi:                     { label: 'CPI', format: 'ratio' },
  concentrationPct:         { label: 'Concentration', format: 'percentage' },
  planned_budget:            { label: 'Planned Budget', format: 'currency' },
}

/**
 * Returns display metadata for a measure key, including synthesized
 * comparison keys (e.g. "eac_vs_budget") — which inherit the format of
 * the measure being compared (eac's currency format), with a label built
 * from both sides, rather than requiring a separate registry entry per
 * possible comparison pair.
 */
export function getMeasureMetadata(measureKey) {
  if (MEASURE_METADATA[measureKey]) return MEASURE_METADATA[measureKey]

  const comparisonMatch = measureKey.match(/^(.+)_vs_(.+)$/)
  if (comparisonMatch) {
    const [, measure, baseline] = comparisonMatch
    const measureMeta = MEASURE_METADATA[measure]
    const baselineMeta = MEASURE_METADATA[baseline]
    if (measureMeta) {
      return {
        label: `${measureMeta.label} vs ${baselineMeta?.label || baseline}`,
        format: measureMeta.format,
      }
    }
  }

  throw new Error(`No metadata registered for measure: "${measureKey}"`)
}

// ─── AnalyticalQuery shape (documentation, not a runtime type) ────────────
//
// AnalyticalQuery {
//   dataset:    string                      (must be a key in DATASETS)
//   measures:   string[]                    (must all exist on the dataset)
//   dimension:  string | null               (must be in the dataset's dimensions, or null)
//   comparison: { type: 'variance', baseline: string } | null
//   time:       { mode: string, aggregation: string } | null   (not used in Phase 1/2)
//   filters:    Record<string, string | string[]>  (shape only — not
//               validated against live data. Multi-select Filter
//               Contract, Stage 2: a string is single-value equality,
//               a string[] is OR-selection, 'all'/undefined means no
//               filter — resolved identically by every Adapter's
//               matchesFilter()/matchesAnyFilter().)
// }

/**
 * Validates the shape and registry-membership of an AnalyticalQuery.
 * Does not touch any data source. Returns { valid, errors }.
 */
export function validateQuery(query) {
  const errors = []

  if (!query || typeof query !== 'object') {
    return { valid: false, errors: ['Query must be an object'] }
  }

  const dataset = DATASETS[query.dataset]
  if (!dataset) {
    errors.push(`Unknown dataset: "${query.dataset}"`)
    // Nothing else can be validated meaningfully without a known dataset.
    return { valid: false, errors }
  }

  if (!Array.isArray(query.measures) || query.measures.length === 0) {
    errors.push('measures must be a non-empty array')
  } else {
    for (const m of query.measures) {
      if (!dataset.measures.includes(m)) {
        errors.push(`Measure "${m}" is not available on dataset "${query.dataset}"`)
      }
    }
  }

  if (query.dimension != null && !dataset.dimensions.includes(query.dimension)) {
    errors.push(`Dimension "${query.dimension}" is not available on dataset "${query.dataset}"`)
  }

  if (query.comparison != null) {
    const comp = query.comparison
    if (!COMPARISON_TYPES[comp.type]) {
      errors.push(`Comparison type "${comp.type}" is not implemented in v1`)
    } else if (!dataset.measures.includes(comp.baseline)) {
      errors.push(`Comparison baseline "${comp.baseline}" is not a measure on dataset "${query.dataset}"`)
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Constructs a well-formed AnalyticalQuery object. Throws if invalid —
 * callers (e.g. the Question Builder UI, later phases) are expected to
 * validate user input before reaching this point; this is a guard, not
 * a UI-facing error reporter.
 */
export function buildQuery({ dataset, measures, dimension = null, comparison = null, time = null, filters = {} }) {
  const query = { dataset, measures, dimension, comparison, time, filters }
  const { valid, errors } = validateQuery(query)
  if (!valid) {
    throw new Error(`Invalid AnalyticalQuery: ${errors.join('; ')}`)
  }
  return query
}
